/**
 * VaultDataContext — shared data + selection state for the DATA CENTER tabs.
 *
 * Loading strategy (lazy, schema-by-schema):
 *
 *   1. On mount we fire `listVaultSchemas()` only — a cheap top-level
 *      catalog of `(schema_name, connector, table_count)`. The DATA
 *      CENTER sidebar paints from this list immediately.
 *   2. As the user expands a schema in the sidebar, we fire
 *      `listVaultTables(schema)` for that schema and cache the result
 *      in `tablesBySchema`. Schemas the user never expands stay
 *      unfetched.
 *   3. The full table/column payload (plus warehouse FK edges) is loaded
 *      lazily via `ensureErd()`, called by the Catalog tab's right-pane
 *      detail view. The sidebar never blocks on it.
 *
 * Layer ('staging' | 'bronze' | 'silver' | 'gold' | 'legacy') is
 * derived from the schema-name prefix (`<layer>__<connector>`), so the
 * Segmented switcher works off `schemasList` alone.
 *
 * State ownership (UI):
 *   layer                 — shared across tabs
 *   selectedSchema/Table  — Catalog + Query (single selection)
 *
 * Pages embedded inside DataCenter can read these via `useVaultData()` and
 * skip their own data fetch / sidebar. Standalone pages outside DataCenter
 * still work because the hook returns null (see `useVaultData` fallback).
 */

import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { message } from 'antd';
import {
  getVaultErd, listVaultSchemas, listVaultTables, getVaultResetPreview,
  listConnections,
} from '../services/dataPlatformService';

const VaultDataContext = createContext(null);

const LAYER_FALLBACK_ORDER = ['bronze', 'staging', 'silver', 'gold', 'legacy'];
const KNOWN_LAYERS = new Set(['staging', 'bronze', 'silver', 'gold']);
// Layers shown by default in the Data Center left rail (multi-select).
// Staging is power-user noise and legacy is rare, so neither is on by default.
const DEFAULT_LAYERS = ['bronze', 'silver', 'gold'];

/**
 * Schema names follow `<layer>__<connector>` for medallion schemas.
 * Anything else (e.g. legacy `<tenant>_<ws>_<connector>`) gets the
 * `legacy` layer so it still has a home in the Segmented switcher.
 */
function deriveLayerConnector(schemaName) {
  const idx = (schemaName || '').indexOf('__');
  if (idx > 0) {
    const prefix = schemaName.slice(0, idx);
    if (KNOWN_LAYERS.has(prefix)) {
      return { layer: prefix, connector: schemaName.slice(idx + 2) };
    }
  }
  return { layer: 'legacy', connector: schemaName || '' };
}

// ── Pure helpers extracted from the `selectedSchemaName`/`selectedSchemaLayer`
// resolution below — that useMemo callback was independently flagged for
// cyclomatic complexity (17) since each nested function gets its own
// score. Split into the 3 documented resolution steps (exact catalog row
// → connector/layer candidate → naming-convention fallback), each with a
// much smaller complexity budget. Same three-way `||` fallback chains as
// before (`row.layer || selectedLayer || layer`, etc.) — just split across
// a helper's return value and the orchestrator's own `|| layer` so the
// net expression is unchanged.

// Shared tie-break: prefer the single candidate, else the one matching the
// tracked selection layer, else just the first.
function pickByLayer(rows, selectedLayer) {
  if (rows.length === 1) {return rows[0];}
  const byLayer = rows.find((r) => (r.layer || 'legacy') === selectedLayer);
  if (byLayer) {return byLayer;}
  return rows.length ? rows[0] : null;
}

// Step 1: exact table row from the loaded catalog (layerTables carries
// column metadata / ERD-sourced rows when available).
export function resolveExactTableRow({ selectedSchema, selectedTable, selectedLayer, layerTables }) {
  const row = pickByLayer(
    (layerTables || []).filter((t) => t.connector === selectedSchema && t.table === selectedTable),
    selectedLayer,
  );
  if (!row?.schema_name) {return null;}
  return { selectedSchemaName: row.schema_name, selectedSchemaLayer: row.layer || selectedLayer };
}

// Step 2: which schema does this connector live in, among the shown layers?
export function resolveConnectorSchemaCandidate({ selectedSchema, selectedLayer, schemasList, layers }) {
  const cand = pickByLayer(
    (schemasList || []).filter((s) => s.connector === selectedSchema && layers.includes(s.layer || 'legacy')),
    selectedLayer,
  );
  if (!cand?.schema_name) {return null;}
  return { selectedSchemaName: cand.schema_name, selectedSchemaLayer: cand.layer || selectedLayer };
}

// Step 3 (last resort): the `<layer>__<connector>` medallion convention.
export function resolveSchemaByConvention({ selectedSchema, selectedLayer, layer }) {
  const lyr = selectedLayer || layer;
  const name = lyr === 'legacy' ? selectedSchema : `${lyr}__${selectedSchema}`;
  return { selectedSchemaName: name, selectedSchemaLayer: lyr };
}

/**
 * Resolve the *physical* warehouse schema for the current (connector,
 * table) selection — see the docstring at the `selectedSchemaName` call
 * site for why this can't just be `<layers[0]>__<connector>`. Tries, in
 * order of confidence: an exact catalog row, a connector/layer candidate,
 * then the naming convention — deferring (empty name) only while the
 * catalog is still loading, so we don't fire a doomed read against a
 * guessed schema before the real one can be resolved.
 */
export function resolveSelectedSchemaResolution({
  selectedSchema, selectedTable, selectedLayer, layerTables, schemasList, schemasLoading, layers, layer,
}) {
  if (!selectedSchema || !selectedTable) {
    return { selectedSchemaName: '', selectedSchemaLayer: '' };
  }
  const exact = resolveExactTableRow({ selectedSchema, selectedTable, selectedLayer, layerTables });
  if (exact) {return { ...exact, selectedSchemaLayer: exact.selectedSchemaLayer || layer };}
  const candidate = resolveConnectorSchemaCandidate({ selectedSchema, selectedLayer, schemasList, layers });
  if (candidate) {return { ...candidate, selectedSchemaLayer: candidate.selectedSchemaLayer || layer };}
  // Defer rather than guess while the catalog is still loading — on a cold
  // deep-link the connector isn't in `schemasList` yet, and guessing
  // layers[0] here would fire a doomed read (404) and flash a warning
  // before the catalog arrives and we can resolve the real schema.
  if (schemasLoading) {
    return { selectedSchemaName: '', selectedSchemaLayer: selectedLayer || layer };
  }
  return resolveSchemaByConvention({ selectedSchema, selectedLayer, layer });
}

export function VaultDataProvider({ children }) {
  // Lightweight top-level catalog for the sidebar. Loaded eagerly.
  const [schemasList, setSchemasList] = useState(null); // null = loading
  const [schemasLoading, setSchemasLoading] = useState(true);

  // Per-schema table cache, populated lazily on tree expand.
  const [tablesBySchema, setTablesBySchema] = useState({});
  const [tablesLoading, setTablesLoading] = useState({});
  // In-flight de-dup so two near-simultaneous expands of the same
  // schema don't issue two HTTP requests.
  const inflightRef = useRef(new Map());

  // Full table/column payload (+ warehouse FKs). Loaded lazily — only
  // when a consumer (Catalog detail pane) actually needs it.
  const [erd, setErd] = useState(null);
  const [erdLoading, setErdLoading] = useState(false);
  const erdInflightRef = useRef(null);

  // Medallion layers shown in the left rail — MULTI-select. Default to
  // bronze + silver + gold; the sidebar chips toggle membership.
  const [layers, setLayersRaw] = useState(DEFAULT_LAYERS);

  // "Display only connected apps" filter + the set of connectors that
  // currently have a configured connection (used to honour it).
  const [showConnectedOnly, setShowConnectedOnly] = useState(false);
  const [connectedConnectors, setConnectedConnectors] = useState(() => new Set());

  // Single-select state (Catalog + Query tabs)
  const [selectedSchema, setSelectedSchema] = useState('');
  const [selectedTable, setSelectedTable] = useState('');
  // The medallion layer of the currently-selected table. Tracked separately
  // because `layer` (= layers[0]) is just the FIRST of the multi-selected
  // layers and can differ from the layer the clicked table actually lives in
  // (e.g. bronze+silver+gold all shown, user clicks a gold table). Consumers
  // use this to resolve the right physical `<layer>__<connector>` schema.
  const [selectedLayer, setSelectedLayer] = useState('');

  // Reset-dialog preview cache (see ResetVaultDialog).
  const [resetPreview, setResetPreview] = useState(null);
  const [resetPreviewLoading, setResetPreviewLoading] = useState(false);
  const loadResetPreview = useCallback(async ({ force = false } = {}) => {
    if (!force && resetPreview) {return resetPreview;}
    setResetPreviewLoading(true);
    try {
      const p = await getVaultResetPreview();
      setResetPreview(p);
      return p;
    } finally {
      setResetPreviewLoading(false);
    }
  }, [resetPreview]);
  const invalidateResetPreview = useCallback(() => setResetPreview(null), []);

  // ── Loaders ────────────────────────────────────────────────────
  const loadSchemas = useCallback(async () => {
    setSchemasLoading(true);
    try {
      const rows = await listVaultSchemas();
      setSchemasList((rows || []).map((s) => ({
        ...s,
        ...deriveLayerConnector(s.schema_name),
      })));
    } catch (err) {
      message.warning(`Couldn't load warehouse schemas: ${err.message}`);
      setSchemasList([]);
    } finally {
      setSchemasLoading(false);
    }
  }, []);

  const loadSchemaTables = useCallback(async (schemaName) => {
    if (!schemaName) {return [];}
    if (tablesBySchema[schemaName]) {return tablesBySchema[schemaName];}
    const inflight = inflightRef.current.get(schemaName);
    if (inflight) {return inflight;}

    setTablesLoading((m) => ({ ...m, [schemaName]: true }));
    const p = (async () => {
      try {
        const rows = await listVaultTables(schemaName);
        const { layer: lyr, connector } = deriveLayerConnector(schemaName);
        const decorated = (rows || []).map((t) => ({
          ...t,
          schema_name: schemaName,
          connector,
          layer: lyr,
        }));
        setTablesBySchema((m) => ({ ...m, [schemaName]: decorated }));
        return decorated;
      } catch (err) {
        message.warning(`Couldn't load tables for ${schemaName}: ${err.message}`);
        setTablesBySchema((m) => ({ ...m, [schemaName]: [] }));
        return [];
      } finally {
        setTablesLoading((m) => {
          const next = { ...m }; delete next[schemaName]; return next;
        });
        inflightRef.current.delete(schemaName);
      }
    })();
    inflightRef.current.set(schemaName, p);
    return p;
  }, [tablesBySchema]);

  const ensureErd = useCallback(async () => {
    if (erd) {return erd;}
    if (erdInflightRef.current) {return erdInflightRef.current;}
    setErdLoading(true);
    const p = (async () => {
      try {
        const vaultErd = (await getVaultErd()) || { tables: [], relationships: [] };
        const loaded = {
          tables: vaultErd.tables || [],
          relationships: vaultErd.relationships || [],
        };
        setErd(loaded);
        return loaded;
      } catch (err) {
        message.warning(`Couldn't load warehouse tables: ${err.message}`);
        const safe = { tables: [], relationships: [] };
        setErd(safe);
        return safe;
      } finally {
        setErdLoading(false);
        erdInflightRef.current = null;
      }
    })();
    erdInflightRef.current = p;
    return p;
  }, [erd]);

  // Refresh everything that's currently loaded. Schemas always; tables
  // only for schemas already cached; ERD only if previously loaded.
  const refresh = useCallback(async () => {
    setTablesBySchema({});
    inflightRef.current.clear();
    erdInflightRef.current = null;
    const hadErd = !!erd;
    setErd(null);
    await loadSchemas();
    if (hadErd) {ensureErd();}
  }, [loadSchemas, ensureErd, erd]);

  // Connected apps — the set of connector_names with a configured
  // connection. Drives the sidebar's "Display only connected apps"
  // filter. Best-effort: an empty set just means the filter hides
  // everything until connections load (the checkbox defaults off).
  const loadConnections = useCallback(async () => {
    try {
      const rows = await listConnections();
      const names = new Set(
        (rows || [])
          .map((c) => c.connector_name || c.connector || c.source)
          .filter(Boolean),
      );
      setConnectedConnectors(names);
    } catch {
      // Non-fatal: leave the set as-is; the filter degrades gracefully.
    }
  }, []);

  useEffect(() => { loadSchemas(); }, [loadSchemas]);
  useEffect(() => { loadConnections(); }, [loadConnections]);

  // ── Derived views ──────────────────────────────────────────────
  // Layer counts come from the lightweight schemas list — no need to
  // wait for the full ERD before populating the Segmented switcher.
  const layerCounts = useMemo(() => {
    const m = new Map();
    for (const s of schemasList || []) {
      const k = s.layer || 'legacy';
      m.set(k, (m.get(k) || 0) + (s.table_count || 0));
    }
    return m;
  }, [schemasList]);

  // Auto-fall back to a non-empty layer only when NONE of the selected
  // layers have tables (e.g. a brand-new workspace with only bronze).
  useEffect(() => {
    if (!schemasList || schemasList.length === 0) {return;}
    const anySelectedHasTables = layers.some((l) => layerCounts.get(l));
    if (!anySelectedHasTables) {
      const fallback = LAYER_FALLBACK_ORDER.find((l) => layerCounts.get(l));
      if (fallback && !layers.includes(fallback)) {setLayersRaw([fallback]);}
    }
  }, [schemasList, layerCounts, layers]);

  const layerSchemas = useMemo(
    () => (schemasList || []).filter((s) => layers.includes(s.layer || 'legacy')),
    [schemasList, layers],
  );

  // Tables in the active layer — prefer ERD when loaded (carries column
  // metadata the sidebar's lightweight rows don't), otherwise union of
  // each loaded schema's lazy table list.
  const layerTables = useMemo(() => {
    if (erd?.tables?.length) {
      return erd.tables.filter((t) => layers.includes(t.layer || 'legacy'));
    }
    const out = [];
    for (const s of layerSchemas) {
      const cached = tablesBySchema[s.schema_name];
      if (cached) {out.push(...cached);}
    }
    return out;
  }, [layerSchemas, tablesBySchema, erd, layers]);

  // Set the selected layer set, pruning any table selection that belonged
  // to a layer that's no longer shown (so the Catalog selection stays
  // consistent without a blanket clear on every toggle).
  const setLayers = useCallback((next) => {
    const arr = Array.from(new Set(Array.isArray(next) ? next : [next]));
    setLayersRaw(arr);
    const allowed = new Set(arr);
    setSelectedSchema((s) => (s && allowed.has(deriveLayerConnector(s).layer) ? s : ''));
    setSelectedTable((t) => {
      // selectedTable is paired with selectedSchema; clear both together.
      const s = selectedSchema;
      return (s && allowed.has(deriveLayerConnector(s).layer) ? t : '');
    });
    // Drop the tracked table layer once it leaves the shown set.
    setSelectedLayer((l) => (l && allowed.has(l) ? l : ''));
  }, [selectedSchema]);

  // Toggle one layer in/out of the shown set. Never lets the set go empty
  // (re-clicking the last selected layer is a no-op) so the tree always
  // has something to show.
  const toggleLayer = useCallback((lyr) => {
    setLayersRaw((prev) => {
      const has = prev.includes(lyr);
      const next = has ? prev.filter((l) => l !== lyr) : [...prev, lyr];
      return next.length ? next : prev;
    });
  }, []);

  // Back-compat single-layer accessors for any consumer still on the old
  // API (standalone pages). `layer` = first selected; `setLayer` collapses
  // the selection to one layer.
  const layer = layers[0] || 'bronze';
  const setLayer = setLayers;

  // Resolve the *physical* warehouse schema for the current selection.
  //
  // The selection is keyed by (connector, table), but the schema a table
  // lives in is `<layer>__<connector>` — and with several layers shown at
  // once the right layer is NOT `layers[0]`. Naively defaulting to the
  // first shown layer sends reads to a non-existent schema (e.g. a gold
  // table resolved as `bronze__<connector>` → "table not found"). Resolve
  // in order of confidence:
  //   1. the loaded catalog row for this exact (connector, table), which
  //      carries the real schema_name (disambiguated by the clicked layer);
  //   2. the schema(s) this connector actually has among the shown layers
  //      (single hit is unambiguous even before any tables are loaded);
  //   3. the `<layer>__<connector>` convention as a last resort.
  const { selectedSchemaName, selectedSchemaLayer } = useMemo(
    () => resolveSelectedSchemaResolution({
      selectedSchema, selectedTable, selectedLayer, layerTables, schemasList, schemasLoading, layers, layer,
    }),
    [selectedSchema, selectedTable, selectedLayer, layerTables, schemasList, schemasLoading, layers, layer],
  );

  const value = useMemo(() => ({
    // Lightweight catalog
    schemasList: schemasList || [],
    schemasLoading,
    layerSchemas,
    tablesBySchema,
    tablesLoading,
    loadSchemaTables,
    // Full ERD (lazy)
    erd,
    erdLoading,
    ensureErd,
    tables: erd?.tables || [],
    relationships: erd?.relationships || [],
    layerTables,
    layerCounts,
    loading: schemasLoading,
    refresh,
    // Layer (multi-select) + single-layer back-compat shims
    layers,
    setLayers,
    toggleLayer,
    layer,
    setLayer,
    // Connected-apps filter
    showConnectedOnly,
    setShowConnectedOnly,
    connectedConnectors,
    // Single selection (Catalog + Query)
    selectedSchema,
    selectedTable,
    selectedLayer,
    // Resolved physical schema for the current (connector, table) selection.
    selectedSchemaName,
    selectedSchemaLayer,
    selectTable: (schema, table, lyr) => {
      setSelectedSchema(schema || '');
      setSelectedTable(table || '');
      setSelectedLayer(lyr || '');
    },
    // Reset-preview cache
    resetPreview,
    resetPreviewLoading,
    loadResetPreview,
    invalidateResetPreview,
  }), [
    schemasList, schemasLoading, layerSchemas, tablesBySchema, tablesLoading,
    loadSchemaTables, erd, erdLoading, ensureErd,
    layerTables, layerCounts, refresh,
    layers, setLayers, toggleLayer, layer, setLayer,
    showConnectedOnly, setShowConnectedOnly, connectedConnectors,
    selectedSchema, selectedTable, selectedLayer, selectedSchemaName, selectedSchemaLayer,
    resetPreview, resetPreviewLoading, loadResetPreview, invalidateResetPreview,
  ]);

  return (
    <VaultDataContext.Provider value={value}>
      {children}
    </VaultDataContext.Provider>
  );
}

/**
 * Returns the vault data context, or `null` when no provider is mounted
 * (so pages that are also rendered outside DATA CENTER — e.g. legacy
 * routes — can detect that and fall back to their own fetch).
 */
export function useVaultData() {
  return useContext(VaultDataContext);
}
